import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  getDocs, 
  doc, 
  updateDoc, 
  where, 
  addDoc,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { app, db, auth } from '../firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  UserIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  BriefcaseIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowUturnLeftIcon
} from '@heroicons/react/24/outline';

const functions = getFunctions(app);
const adminDeleteUser = httpsCallable(functions, 'adminDeleteUser');
const refundJobFn = httpsCallable(functions, 'refundJob');
const adminDeleteJobFn = httpsCallable(functions, 'adminDeleteJob');

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('jobs');
  const [searchTerm, setSearchTerm] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  const [jobStatusFilter, setJobStatusFilter] = useState('all');
  const [jobSortOrder, setJobSortOrder] = useState('newest');
  const [showAddSitterModal, setShowAddSitterModal] = useState(false);
  const [newSitterEmail, setNewSitterEmail] = useState('');
  const [newSitterName, setNewSitterName] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [approvedSitters, setApprovedSitters] = useState([]);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchJobs(), fetchApprovedSitters(), fetchApplications()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const fetchUsers = async () => {
    try {
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      const usersData = usersSnapshot.docs.map((doc) => {
        const data = doc.data();
        const createdAtFallback =
          doc.createTime && typeof doc.createTime.toDate === 'function'
            ? doc.createTime.toDate()
            : null;
        const updatedAtFallback =
          doc.updateTime && typeof doc.updateTime.toDate === 'function'
            ? doc.updateTime.toDate()
            : null;

        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt || createdAtFallback,
          updatedAt: data.updatedAt || updatedAtFallback,
          lastLoginAt: data.lastLoginAt || null,
        };
      });
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchJobs = async () => {
    try {
      const jobsQuery = query(collection(db, 'jobs'));
      const jobsSnapshot = await getDocs(jobsQuery);
      
      const jobsData = jobsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setJobs(jobsData);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    }
  };

  const fetchApprovedSitters = async () => {
    try {
      const sittersQuery = query(collection(db, 'approvedSitters'));
      const sittersSnapshot = await getDocs(sittersQuery);
      
      const sittersData = sittersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setApprovedSitters(sittersData);
    } catch (error) {
      console.error('Error fetching approved sitters:', error);
      setApprovedSitters([]);
    }
  };

  const fetchApplications = async () => {
    try {
      const applicationsQuery = query(collection(db, 'applications'));
      const applicationsSnapshot = await getDocs(applicationsQuery);
      
      const applicationsData = applicationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setApplications(applicationsData);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setApplications([]);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await adminDeleteUser({ userId });
        setUsers(users.filter(user => user.id !== userId));
        alert('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error deleting user');
      }
    }
  };

  const handleDeleteJob = async (jobId) => {
    // Guard: don't allow deleting a job that has a Paid application. Losing that
    // record makes it impossible to audit or refund the Stripe transfer.
    const paidApp = getPaidApplicationForJob(jobId);
    if (paidApp) {
      alert('Cannot delete this job — it has a Paid application. Issue a refund first, then delete.');
      return;
    }

    // Stronger warning when the job is actually booked (or has a pending offer).
    const isBooked = applications.some(
      (a) => a.jobId === jobId && ['Active', 'Awaiting Sitter Confirmation'].includes(a.status)
    );
    const confirmMsg = isBooked
      ? '⚠️ This job is BOOKED — a sitter has confirmed or has a pending offer.\n\nDeleting will cancel it for both parties. Each will receive an email explaining that Handpicked Nanny cancelled the job, and NO cancellation fee will be charged to anyone.\n\nAll related applications and messages will be permanently deleted. Continue?'
      : 'Are you sure you want to delete this job? Related applications and messages will also be deleted, and any pending applicants will be emailed that the listing was removed. This cannot be undone.';

    if (window.confirm(confirmMsg)) {
      try {
        // Server-side delete: verifies admin, emails affected users BEFORE
        // deleting, and cleans up applications + messages in chunked batches.
        const res = await adminDeleteJobFn({ jobId });
        if (res.data?.success) {
          setJobs(jobs.filter(job => job.id !== jobId));
          setApplications(applications.filter(application => application.jobId !== jobId));
          alert(`Job deleted — ${res.data.deletedDocs} record(s) removed${res.data.emailsSent ? `, ${res.data.emailsSent} notification email(s) sent` : ''}.`);
        } else {
          alert('Delete failed: ' + (res.data?.message || 'unknown error'));
        }
      } catch (error) {
        console.error('Error deleting job:', error);
        alert('Error deleting job: ' + (error.message || 'unknown error'));
      }
    }
  };

  const handlePreviewRefund = async (application) => {
    if (!application || !application.id) return;
    const adminUid = auth.currentUser?.uid;
    if (!adminUid) {
      alert('You must be logged in as an admin.');
      return;
    }

    try {
      const response = await refundJobFn({ adminUserId: adminUid, applicationId: application.id, dryRun: true });
      if (response.data?.success && response.data.preview) {
        const p = response.data.preview;
        const msg = [
          `PREVIEW ONLY — no money has moved.`,
          ``,
          `Stripe payment intent: ${p.paymentIntentId}`,
          `Status: ${p.paymentIntentStatus}`,
          `Live mode: ${p.livemode ? 'YES (real money)' : 'NO (test mode)'}`,
          ``,
          `If you issue the refund:`,
          `  • Parent gets back: $${p.wouldRefundToParent.toFixed(2)}`,
          `  • Sitter loses (transfer reversed): $${p.wouldReverseTransferFromSitter.toFixed(2)}`,
          `  • Platform fee reversed: $${p.wouldReverseApplicationFee.toFixed(2)}`,
          `  • Platform absorbs (Stripe fee, approx): $${p.approxNonRefundableStripeFee.toFixed(2)}`,
          ``,
          `Stripe flags used:`,
          `  • reverse_transfer: ${p.flagsThatWillBeUsed.reverse_transfer}`,
          `  • refund_application_fee: ${p.flagsThatWillBeUsed.refund_application_fee}`,
        ].join('\n');
        alert(msg);
      } else {
        alert('Preview failed: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error previewing refund:', error);
      alert('Error previewing refund: ' + (error.message || 'Unknown error'));
    }
  };

  const handleRefundJob = async (application) => {
    if (!application || !application.id) return;
    const adminUid = auth.currentUser?.uid;
    if (!adminUid) {
      alert('You must be logged in as an admin to issue refunds.');
      return;
    }

    const confirmMsg = `Issue a full refund for this job?\n\n• Parent will receive the original charge back to their card\n• Sitter's payout will be reversed\n• Platform fee will be refunded\n\nThis cannot be undone.\n\nTip: use the eye icon next to Refund to preview first.`;
    if (!window.confirm(confirmMsg)) return;

    const reason = window.prompt('Optional: reason for the refund (for your records)') || '';

    try {
      const response = await refundJobFn({ adminUserId: adminUid, applicationId: application.id, reason });
      if (response.data?.success) {
        alert(response.data.message || 'Refund issued successfully.');
        setApplications((prev) => prev.map((a) => a.id === application.id ? { ...a, status: 'Refunded' } : a));
      } else {
        alert('Refund failed: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error issuing refund:', error);
      alert('Error issuing refund: ' + (error.message || 'Unknown error'));
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isActive: !currentStatus,
        updatedAt: serverTimestamp()
      });
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, isActive: !currentStatus }
          : user
      ));
      alert(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Error updating user status');
    }
  };

  const handleAddSitter = async () => {
    const email = newSitterEmail.trim();
    const name = newSitterName.trim();
    if (!email || !name) {
      alert('Please fill in all fields');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert('Please enter a valid email address.');
      return;
    }

    try {
      await addDoc(collection(db, 'approvedSitters'), {
        email: email.toLowerCase(),
        name,
        addedAt: serverTimestamp(),
        addedBy: 'admin'
      });
      
      setNewSitterEmail('');
      setNewSitterName('');
      setShowAddSitterModal(false);
      alert('Sitter added to approved list successfully');
      
      await fetchApprovedSitters();
    } catch (error) {
      console.error('Error adding sitter:', error);
      alert('Error adding sitter: ' + error.message);
    }
  };

  const getApplicationForJob = (jobId) => {
    return applications.find(app => app.jobId === jobId && (app.status === 'Completed' || app.status === 'Paid'));
  };

  const getPaidApplicationForJob = (jobId) => {
    return applications.find(app => app.jobId === jobId && app.status === 'Paid');
  };

  const getApplicationForJobAnyStatus = (jobId) => {
    // Prefer the "active" one (any status that's not a terminal decline).
    return applications.find(app => app.jobId === jobId && !['Denied', 'Family Denied', 'Sitter Denied'].includes(app.status))
      || applications.find(app => app.jobId === jobId);
  };

  const anyApplicationHasStatus = (jobId, status) => {
    return applications.some(app => app.jobId === jobId && app.status === status);
  };

  // The application that actually "owns" the job right now (offer out, booked,
  // completed, or paid). Denied/rescinded applications never count — so a
  // sitter who declined no longer shows as the job's sitter.
  const getEngagedApplicationForJob = (jobId) => {
    return applications.find(app => app.jobId === jobId &&
      ['Active', 'Awaiting Sitter Confirmation', 'Completed', 'Paid'].includes(app.status));
  };

  // Display text for the "Sitter" column: the engaged sitter's name, or an
  // applicant count while the job is still open, or nothing.
  const getSitterDisplayForJob = (jobId) => {
    const engaged = getEngagedApplicationForJob(jobId);
    if (engaged) return getSitterName(engaged);
    const pendingCount = applications.filter(app => app.jobId === jobId && app.status === 'Pending').length;
    if (pendingCount > 0) return `${pendingCount} applicant${pendingCount > 1 ? 's' : ''} pending`;
    return 'No sitter assigned';
  };

  const getSitterName = (application) => {
    if (!application || !application.sitterId) return 'No sitter assigned';
    
    const sitter = users.find(user => user.id === application.sitterId);
    return sitter ? sitter.name : 'Unknown sitter';
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = userTypeFilter === 'all' || user.userType === userTypeFilter;
    return matchesSearch && matchesType;
  });

  const filteredJobs = jobs.filter(job => {
    // Searchable sitter text: engaged sitter's name plus every pending applicant's name
    const sitterName = getSitterDisplayForJob(job.id) + ' ' +
      applications.filter(a => a.jobId === job.id && a.status === 'Pending')
        .map(a => a.sitterName || '').join(' ');
    
    const matchesSearch = job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.parentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sitterName.toLowerCase().includes(searchTerm.toLowerCase());
    
    // A job is "unfilled" (i.e. a candidate for active/inactive) only if it has
    // NO engaged application (booked/completed/paid) AND no terminal one
    // (cancelled/refunded). Using the real persisted status strings.
    const engaged = getEngagedApplicationForJob(job.id); // Active/Awaiting/Completed/Paid
    const hasTerminal = engaged ||
      anyApplicationHasStatus(job.id, 'Parent Cancelled') ||
      anyApplicationHasStatus(job.id, 'Sitter Cancelled') ||
      anyApplicationHasStatus(job.id, 'Cancelled') ||
      anyApplicationHasStatus(job.id, 'Refunded');
    const isUnfilled = !hasTerminal;
    const application = getApplicationForJob(job.id);
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const isExpired = job.endDate ? new Date(job.endDate + 'T23:59:59') < todayStart : false;

    const matchesStatus = jobStatusFilter === 'all' ||
                         (jobStatusFilter === 'active' && isUnfilled && !isExpired) ||
                         (jobStatusFilter === 'inactive' && isUnfilled && isExpired) ||
                         (jobStatusFilter === 'completed' && application && (application.status === 'Completed' || application.status === 'Paid')) ||
                         // Cancelled uses ALL applications, not just Paid/Completed ones,
                         // and matches the actual persisted status strings.
                         (jobStatusFilter === 'cancelled' && (
                           anyApplicationHasStatus(job.id, 'Parent Cancelled') ||
                           anyApplicationHasStatus(job.id, 'Sitter Cancelled') ||
                           anyApplicationHasStatus(job.id, 'Cancelled')
                         ));
    
    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    const getTime = (job) => {
      const ts = job.createdAt || job.datePosted || job.startDate;
      if (!ts) return 0;
      if (ts.toDate) return ts.toDate().getTime();
      return new Date(ts).getTime();
    };
    return jobSortOrder === 'newest' ? getTime(b) - getTime(a) : getTime(a) - getTime(b);
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    let date;
    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      date = new Date(timestamp);
    }

    if (!date || Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  // Format a YYYY-MM-DD string into "Fri, Jul 5" style (uses local timezone-safe parsing)
  const formatScheduledDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      if (Number.isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Format "HH:MM" 24h string to "h:MM AM/PM"
  const formatTimeToAMPM = (time24) => {
    if (!time24 || typeof time24 !== 'string' || !time24.includes(':')) return 'N/A';
    const [hStr, mStr] = time24.split(':');
    const h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return 'N/A';
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
  };

  const getJobStatusIcon = (status) => {
    switch (status) {
      case 'Completed':
      case 'Paid':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case 'Cancelled':
      case 'Parent Cancelled':
      case 'Sitter Cancelled':
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      case 'Awaiting Sitter Confirmation':
        return <ClockIcon className="w-5 h-5 text-yellow-600" />;
      case 'Expired':
        return <XCircleIcon className="w-5 h-5 text-gray-500" />;
      case 'Active':
      case 'active':
      default:
        return <ClockIcon className="w-5 h-5 text-blue-600" />;
    }
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'Completed':
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Cancelled':
      case 'Parent Cancelled':
      case 'Sitter Cancelled':
        return 'bg-red-100 text-red-800';
      case 'Awaiting Sitter Confirmation':
        return 'bg-yellow-100 text-yellow-800';
      case 'Expired':
        return 'bg-gray-200 text-gray-700';
      case 'Active':
      case 'active':
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const calculatePaymentBreakdown = (application) => {
    if (!application || !application.totalHoursWorked || !application.jobPayRange) {
      return null;
    }

    const hours = Number(application.totalHoursWorked) || 0;
    const rate = Number(application.jobPayRange) || 0;
    const basePay = hours * rate; // Sitter's base pay (hours × rate)
    const tipAmount = Number(application.tipAmount) || 0;

    // Prefer the exact values the backend wrote at payment time; fall back to
    // recomputing only if the field is missing (e.g., pre-fix historical data).
    const platformFee = application.platformFee !== undefined
      ? Number(application.platformFee)
      : basePay * 0.10;
    const processingFee = application.processingFee !== undefined
      ? Number(application.processingFee)
      : Math.ceil((0.0344 * basePay * 100 + 0.0315 * tipAmount * 100 + 55) / 0.971) / 100;
    const totalPlatformAmount = application.totalPlatformFee !== undefined
      ? Number(application.totalPlatformFee)
      : platformFee + processingFee;
    const sitterPayout = application.sitterPayout !== undefined
      ? Number(application.sitterPayout)
      : basePay + tipAmount;
    const totalCharged = application.totalCharge !== undefined
      ? Number(application.totalCharge)
      : basePay + platformFee + processingFee + tipAmount;

    return {
      basePay: basePay.toFixed(2),
      tipAmount: tipAmount.toFixed(2),
      hasTip: tipAmount > 0,
      platformFee: platformFee.toFixed(2),
      processingFee: processingFee.toFixed(2),
      totalPlatformAmount: totalPlatformAmount.toFixed(2),
      sitterPayout: sitterPayout.toFixed(2),
      totalCharged: totalCharged.toFixed(2),
      hoursWorked: hours,
      hourlyRate: rate,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                Admin Panel
              </h1>
              <p className="text-gray-600 mt-2">Manage users, jobs, and platform settings</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">{jobs.length}</div>
              <div className="text-sm text-gray-500">Total Jobs</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('jobs')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'jobs'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <BriefcaseIcon className="w-5 h-5 inline mr-2" />
                Jobs ({jobs.length})
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <UserIcon className="w-5 h-5 inline mr-2" />
                Users ({users.length})
              </button>
              <button
                onClick={() => setActiveTab('sitters')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'sitters'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <CheckIcon className="w-5 h-5 inline mr-2" />
                Approved Sitters
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {activeTab === 'users' && (
                <select
                  value={userTypeFilter}
                  onChange={(e) => setUserTypeFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="parent">Parents</option>
                  <option value="sitter">Sitters</option>
                  <option value="admin">Admins</option>
                </select>
              )}
              {activeTab === 'jobs' && (
                <>
                  <select
                    value={jobStatusFilter}
                    onChange={(e) => setJobStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <select
                    value={jobSortOrder}
                    onChange={(e) => setJobSortOrder(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                  </select>
                </>
              )}
              {activeTab === 'sitters' && (
                <button
                  onClick={() => setShowAddSitterModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add Sitter
                </button>
              )}
            </div>

            {/* Jobs Tab Content */}
            {activeTab === 'jobs' && (
              <div className="space-y-4">
                {/* Job Statistics Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-4">Job Statistics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{jobs.length}</div>
                      <div className="text-sm text-blue-700">Total Jobs</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {jobs.filter(job => {
                          const application = getApplicationForJob(job.id);
                          return application && (application.status === 'Completed' || application.status === 'Paid');
                        }).length}
                      </div>
                      <div className="text-sm text-green-700">Completed/Paid</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {jobs.filter(job => {
                          const hasTerminal = getEngagedApplicationForJob(job.id) ||
                            anyApplicationHasStatus(job.id, 'Parent Cancelled') ||
                            anyApplicationHasStatus(job.id, 'Sitter Cancelled') ||
                            anyApplicationHasStatus(job.id, 'Cancelled') ||
                            anyApplicationHasStatus(job.id, 'Refunded');
                          const todayStart = new Date();
                          todayStart.setHours(0, 0, 0, 0);
                          const isExpired = job.endDate ? new Date(job.endDate + 'T23:59:59') < todayStart : false;
                          return !hasTerminal && !isExpired;
                        }).length}
                      </div>
                      <div className="text-sm text-blue-700">Active</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {jobs.filter(job =>
                          anyApplicationHasStatus(job.id, 'Parent Cancelled') ||
                          anyApplicationHasStatus(job.id, 'Sitter Cancelled') ||
                          anyApplicationHasStatus(job.id, 'Cancelled')
                        ).length}
                      </div>
                      <div className="text-sm text-red-700">Cancelled</div>
                    </div>
                  </div>
                  
                  {/* Revenue Summary for Completed Jobs */}
                  {(() => {
                    const completedJobs = jobs.filter(job => {
                      const application = getApplicationForJob(job.id);
                      return application && (application.status === 'Completed' || application.status === 'Paid');
                    });
                    const totalRevenue = completedJobs.reduce((sum, job) => {
                      const application = getApplicationForJob(job.id);
                      const breakdown = calculatePaymentBreakdown(application);
                      return sum + (breakdown ? parseFloat(breakdown.totalCharged) : 0);
                    }, 0);
                    const totalPlatformFees = completedJobs.reduce((sum, job) => {
                      const application = getApplicationForJob(job.id);
                      const breakdown = calculatePaymentBreakdown(application);
                      return sum + (breakdown ? parseFloat(breakdown.totalPlatformAmount) : 0);
                    }, 0);
                    
                    if (completedJobs.length > 0) {
                      return (
                        <div className="mt-4 pt-4 border-t border-blue-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="text-center">
                              <div className="text-xl font-bold text-green-600">${totalRevenue.toFixed(2)}</div>
                              <div className="text-sm text-green-700">Total Revenue</div>
                            </div>
                            <div className="text-center">
                              <div className="text-xl font-bold text-purple-600">${totalPlatformFees.toFixed(2)}</div>
                              <div className="text-sm text-purple-700">Platform Fees</div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                {filteredJobs.map((job) => {
                  const application = getApplicationForJob(job.id);
                  // Only an engaged application (offer out / booked / completed / paid)
                  // counts as "the sitter" — declined or rescinded ones don't.
                  const anyApplication = getEngagedApplicationForJob(job.id);
                  const paymentBreakdown = calculatePaymentBreakdown(application);
                  const sitterName = getSitterDisplayForJob(job.id);

                  // Display status: an engaged application's status wins; otherwise
                  // an UNFILLED job whose end date has passed reads "Expired"
                  // instead of the stale "Open".
                  const displayStatus = (() => {
                    if (anyApplication) return anyApplication.status;
                    const todayStart = new Date();
                    todayStart.setHours(0, 0, 0, 0);
                    const isExpired = job.endDate ? new Date(job.endDate + 'T23:59:59') < todayStart : false;
                    if (isExpired) return 'Expired';
                    return job.status || 'Open';
                  })();

                  return (
                    <div key={job.id} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3 flex-wrap gap-y-1">
                            <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
                            <div className="flex items-center space-x-2">
                              {getJobStatusIcon(displayStatus)}
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getJobStatusColor(displayStatus)}`}>
                                {displayStatus}
                              </span>
                            </div>
                          </div>
                          
                          <p className="text-gray-600 mb-4">{job.description}</p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                            <div>
                              <span className="text-sm font-medium text-gray-500">Parent:</span>
                              <p className="text-gray-900">{job.parentName}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Sitter:</span>
                              {(() => {
                                // No engaged sitter yet — list every pending applicant by name.
                                const pendingApps = !anyApplication
                                  ? applications.filter(a => a.jobId === job.id && a.status === 'Pending')
                                  : [];
                                if (pendingApps.length > 0) {
                                  return (
                                    <div>
                                      <p className="text-xs text-gray-500 mb-0.5">{pendingApps.length} applicant{pendingApps.length > 1 ? 's' : ''} pending:</p>
                                      <ul className="space-y-0.5">
                                        {pendingApps.map(app => (
                                          <li key={app.id}>
                                            <a
                                              href={`/profile/${app.sitterId}`}
                                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                            >
                                              {app.sitterName || 'Unknown sitter'}
                                            </a>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  );
                                }
                                return <p className="text-gray-900">{sitterName}</p>;
                              })()}
                              {anyApplication && anyApplication.status === 'Awaiting Sitter Confirmation' && (
                                <p className="text-xs text-yellow-700 mt-0.5">
                                  ⏳ Offer sent {anyApplication.offerSentAt ? formatDate(anyApplication.offerSentAt) : '(time not recorded)'}
                                  {anyApplication.offerSentAt && (() => {
                                    const ts = anyApplication.offerSentAt.toDate
                                      ? anyApplication.offerSentAt.toDate()
                                      : new Date(anyApplication.offerSentAt);
                                    if (Number.isNaN(ts.getTime())) return null;
                                    return ` · expires ${formatDate(new Date(ts.getTime() + 24 * 60 * 60 * 1000))}`;
                                  })()}
                                </p>
                              )}
                              {anyApplication && anyApplication.status === 'Active' && (
                                <p className="text-xs text-green-700 mt-0.5">✓ Offer accepted — booked</p>
                              )}
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Rate:</span>
                              <p className="text-gray-900">${job.payRange}/hr</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Location:</span>
                              <p className="text-gray-900">{job.city}, {job.state}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Start Date:</span>
                              <p className="text-gray-900">{formatScheduledDate(job.startDate)}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">End Date:</span>
                              <p className="text-gray-900">{formatScheduledDate(job.endDate)}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Start Time:</span>
                              <p className="text-gray-900">{formatTimeToAMPM(job.jobStartTime)}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-500">Days Scheduled:</span>
                              <p className="text-gray-900">
                                {job.schedule && typeof job.schedule === 'object'
                                  ? `${Object.keys(job.schedule).length} day(s)`
                                  : 'N/A'}
                              </p>
                            </div>
                          </div>

                          {/* Full Schedule (when sitter is booked to work) */}
                          {job.schedule && typeof job.schedule === 'object' && Object.keys(job.schedule).length > 0 && (
                            <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                              <h4 className="text-sm font-semibold text-indigo-900 mb-3 flex items-center">
                                <ClockIcon className="w-4 h-4 mr-2" />
                                Booked Schedule
                              </h4>
                              <div className="space-y-2">
                                {Object.keys(job.schedule).sort().map((dateKey) => {
                                  const ranges = Array.isArray(job.schedule[dateKey])
                                    ? job.schedule[dateKey]
                                    : [job.schedule[dateKey]];
                                  return (
                                    <div key={dateKey} className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white px-3 py-2 rounded border border-indigo-100">
                                      <span className="font-medium text-indigo-900">{formatScheduledDate(dateKey)}</span>
                                      <div className="text-sm text-indigo-700">
                                        {ranges.filter(Boolean).map((r, i) => (
                                          <span key={i}>
                                            {i > 0 && ', '}
                                            {formatTimeToAMPM(r.start)} – {formatTimeToAMPM(r.end)}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Payment Details for Completed/Paid Jobs */}
                          {application && (application.status === 'Completed' || application.status === 'Paid') && paymentBreakdown && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                              <h4 className="text-lg font-semibold text-green-800 mb-3 flex items-center">
                                <CheckCircleIcon className="w-5 h-5 mr-2" />
                                Payment Details
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <div className="flex justify-between">
                                    <span className="text-sm font-medium text-green-700">Hours Worked:</span>
                                    <span className="text-sm font-semibold text-green-800">{paymentBreakdown.hoursWorked} hours</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-sm font-medium text-green-700">Hourly Rate:</span>
                                    <span className="text-sm font-semibold text-green-800">${paymentBreakdown.hourlyRate}/hr</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-sm font-medium text-green-700">Base Pay:</span>
                                    <span className="text-sm font-semibold text-green-800">${paymentBreakdown.basePay}</span>
                                  </div>
                                  {paymentBreakdown.hasTip && (
                                    <div className="flex justify-between">
                                      <span className="text-sm font-medium text-green-700">Tip:</span>
                                      <span className="text-sm font-semibold text-green-800">${paymentBreakdown.tipAmount}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between border-t border-green-200 pt-2">
                                    <span className="text-sm font-medium text-green-700">Total Charged to Parent:</span>
                                    <span className="text-sm font-bold text-green-800">${paymentBreakdown.totalCharged}</span>
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <div className="flex justify-between">
                                    <span className="text-sm font-medium text-green-700">Platform Fee (10%):</span>
                                    <span className="text-sm font-semibold text-green-800">${paymentBreakdown.platformFee}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-sm font-medium text-green-700">Processing Fee:</span>
                                    <span className="text-sm font-semibold text-green-800">${paymentBreakdown.processingFee}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-sm font-medium text-green-700">Platform Total:</span>
                                    <span className="text-sm font-semibold text-green-800">${paymentBreakdown.totalPlatformAmount}</span>
                                  </div>
                                  <div className="flex justify-between border-t border-green-200 pt-2">
                                    <span className="text-sm font-medium text-green-700">Sitter Payout {paymentBreakdown.hasTip && '(incl. tip)'}:</span>
                                    <span className="text-sm font-bold text-green-800">${paymentBreakdown.sitterPayout}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {job.schedule && (
                            <div className="mb-4">
                              <span className="text-sm font-medium text-gray-500">Schedule:</span>
                              <div className="mt-1 text-sm text-gray-700">
                                {Array.isArray(job.schedule) ? job.schedule.length : Object.keys(job.schedule).length} day(s) scheduled
                              </div>
                            </div>
                          )}

                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>Posted: {formatDate(job.createdAt)}</span>
                            {job.updatedAt && (
                              <span>Updated: {formatDate(job.updatedAt)}</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 ml-4">
                          {job.parentId && (
                            <a
                              href={`/profile/${job.parentId}`}
                              className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Parent Profile"
                            >
                              <UserCircleIcon className="w-5 h-5" />
                            </a>
                          )}
                          {anyApplication && anyApplication.sitterId && (
                            <a
                              href={`/profile/${anyApplication.sitterId}`}
                              className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                              title="View Sitter Profile"
                            >
                              <UserCircleIcon className="w-5 h-5" />
                            </a>
                          )}
                          {(() => {
                            // For refund actions, always target the SPECIFIC Paid application
                            // for this job (not the first-match, which could be Withdrawn/Denied).
                            const paidApp = getPaidApplicationForJob(job.id);
                            return paidApp && (
                            <>
                              <button
                                onClick={() => handlePreviewRefund(paidApp)}
                                className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Preview refund (no money moves)"
                              >
                                <EyeIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleRefundJob(paidApp)}
                                className="p-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                                title="Refund Job (full)"
                              >
                                <ArrowUturnLeftIcon className="w-5 h-5" />
                              </button>
                            </>
                            );
                          })()}
                          <button
                            onClick={() => handleDeleteJob(job.id)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Job"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {filteredJobs.length === 0 && (
                  <div className="text-center py-12">
                    <BriefcaseIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No jobs found</p>
                  </div>
                )}
              </div>
            )}

            {/* Users Tab Content */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                {filteredUsers.map((user) => (
                  <div key={user.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{user.name || 'No Name'}</h3>
                          <p className="text-gray-600">{user.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.userType === 'admin' 
                                ? 'bg-red-100 text-red-800'
                                : user.userType === 'parent'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {user.userType?.toUpperCase() || 'UNKNOWN'}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.isActive === false 
                                ? 'bg-red-100 text-red-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {user.isActive === false ? 'INACTIVE' : 'ACTIVE'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <a
                          href={`/profile/${user.id}`}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Profile"
                        >
                          <UserCircleIcon className="w-5 h-5" />
                        </a>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserDetails(true);
                          }}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <EyeIcon className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleToggleUserStatus(user.id, user.isActive)}
                          className={`p-2 rounded-lg transition-colors ${
                            user.isActive === false
                              ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                              : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                          }`}
                          title={user.isActive === false ? 'Activate User' : 'Deactivate User'}
                        >
                          {user.isActive === false ? <EyeIcon className="w-5 h-5" /> : <EyeSlashIcon className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete User"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-gray-500">
                      Joined: {formatDate(user.createdAt)}
                    </div>
                  </div>
                ))}
                {filteredUsers.length === 0 && (
                  <div className="text-center py-12">
                    <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No users found</p>
                  </div>
                )}
              </div>
            )}

            {/* Approved Sitters Tab Content */}
            {activeTab === 'sitters' && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
                    <div>
                      <h3 className="font-semibold text-blue-900">Sitter Pre-approval System</h3>
                      <p className="text-blue-700 text-sm mt-1">
                        Only sitters whose emails are added to this list will be able to sign up. 
                        Parents can sign up without pre-approval.
                      </p>
                    </div>
                  </div>
                </div>
                
                {approvedSitters.length > 0 ? (
                  <div className="space-y-3">
                    {approvedSitters.map((sitter) => (
                      <div key={sitter.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                              {sitter.name?.charAt(0)?.toUpperCase() || 'S'}
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{sitter.name}</h3>
                              <p className="text-gray-600">{sitter.email}</p>
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            Added: {formatDate(sitter.addedAt)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CheckIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No approved sitters yet</p>
                    <p className="text-sm text-gray-400 mt-2">Use the "Add Sitter" button to add approved sitter emails</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Add Sitter Modal */}
        {showAddSitterModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Add Approved Sitter</h3>
                <button
                  onClick={() => setShowAddSitterModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sitter Email
                  </label>
                  <input
                    type="email"
                    value={newSitterEmail}
                    onChange={(e) => setNewSitterEmail(e.target.value)}
                    placeholder="sitter@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sitter Name
                  </label>
                  <input
                    type="text"
                    value={newSitterName}
                    onChange={(e) => setNewSitterName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowAddSitterModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddSitter}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Sitter
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Details Modal */}
        {showUserDetails && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">User Details</h3>
                <button
                  onClick={() => setShowUserDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                    {selectedUser.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900">{selectedUser.name || 'No Name'}</h4>
                    <p className="text-gray-600">{selectedUser.email}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">User Type</label>
                    <p className="text-gray-900">{selectedUser.userType?.toUpperCase() || 'UNKNOWN'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <p className={`font-medium ${
                      selectedUser.isActive === false ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {selectedUser.isActive === false ? 'INACTIVE' : 'ACTIVE'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Joined</label>
                    <p className="text-gray-900">{formatDate(selectedUser.createdAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Updated</label>
                    <p className="text-gray-900">{formatDate(selectedUser.updatedAt)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Login</label>
                    <p className="text-gray-900">{formatDate(selectedUser.lastLoginAt)}</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between mt-6">
                <a
                  href={`/profile/${selectedUser.id}`}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <UserCircleIcon className="w-4 h-4 mr-2" />
                  View Profile
                </a>
                <button
                  onClick={() => setShowUserDetails(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
